import { useSetAtom } from "jotai";
import { type Ref, useImperativeHandle, useRef } from "react";

import { ContactAddressDialog } from "./components/ContactAddressDialog";
import { hideContactAddressAtom, showContactAddressAtom } from "./stores";
import type { LocationAddressDetail, LocationTypedAddress } from "./types";

export interface ContactAddressModalHandle {
  // `contactId` is optional: when given, the dialog fixes the contact and
  // hides the contact picker; when omitted, the user picks a contact
  // themselves.
  open: (
    contactId?: number,
    place?: string,
    originElement?: HTMLElement,
  ) => ContactAddressModalHandle;
  hide: () => ContactAddressModalHandle;
}

export type ContactAddressModalProps = {
  onSubmit?: (address: LocationTypedAddress | LocationAddressDetail) => void;
  onCancel?: () => void;
  ref?: Ref<ContactAddressModalHandle>;
};

function ContactAddressModalContent({
  onSubmit,
  onCancel,
  ref,
}: ContactAddressModalProps) {
  const show = useSetAtom(showContactAddressAtom);
  const hide = useSetAtom(hideContactAddressAtom);
  const originRef = useRef<DOMRect | null>(null);

  const handle: ContactAddressModalHandle = {
    open: (contactId, place, originElement) => {
      originRef.current = originElement?.getBoundingClientRect() ?? null;
      show(contactId ?? null, place ?? "");
      return handle;
    },
    hide: () => {
      hide();
      return handle;
    },
  };

  useImperativeHandle(ref, () => handle, []);

  return (
    <ContactAddressDialog
      onSubmit={onSubmit}
      onCancel={onCancel}
      originRef={originRef}
    />
  );
}

export function ContactAddressModal({
  onSubmit,
  onCancel,
  ref,
}: ContactAddressModalProps) {
  return (
    <ContactAddressModalContent
      onSubmit={onSubmit}
      onCancel={onCancel}
      ref={ref}
    />
  );
}
